import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WinRateChart = ({ deals = [] }) => {
  const [chartType, setChartType] = useState('line');
  const [winRateData, setWinRateData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [metrics, setMetrics] = useState({
    totalWon: 0,
    totalLost: 0,
    winRate: 0,
    bestMonth: { rate: 0, month: 'N/A' }
  });

  useEffect(() => {
    if (!deals || deals?.length === 0) {
      setWinRateData([]);
      setPieData([]);
      setMetrics({ totalWon: 0, totalLost: 0, winRate: 0, bestMonth: { rate: 0, month: 'N/A' } });
      return;
    }

    // Calculate metrics by month
    const monthlyStats = {};
    const currentYear = new Date()?.getFullYear();
    
    deals?.forEach(deal => {
      const dealDate = new Date(deal.created_at);
      const year = dealDate?.getFullYear();
      const month = dealDate?.toLocaleString('en-US', { month: 'short' });
      const key = `${month}`;
      
      if (!monthlyStats?.[key]) {
        monthlyStats[key] = { month: key, won: 0, lost: 0, winRate: 0, year };
      }
      
      if (deal?.stage === 'won') {
        monthlyStats[key].won++;
      } else if (deal?.stage === 'lost') {
        monthlyStats[key].lost++;
      }
    });

    // Calculate win rates
    const monthlyData = Object.values(monthlyStats)?.map(stat => {
      const total = stat?.won + stat?.lost;
      stat.winRate = total > 0 ? Math.round((stat?.won / total) * 100) : 0;
      return stat;
    });

    // Sort by month order
    const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    monthlyData?.sort((a, b) => monthOrder?.indexOf(a?.month) - monthOrder?.indexOf(b?.month));

    setWinRateData(monthlyData);

    // Calculate overall metrics
    const totalWon = deals?.filter(d => d?.stage === 'won')?.length;
    const totalLost = deals?.filter(d => d?.stage === 'lost')?.length;
    const totalClosed = totalWon + totalLost;
    const overallWinRate = totalClosed > 0 ? ((totalWon / totalClosed) * 100)?.toFixed(1) : 0;

    // Find best month
    const bestMonthData = monthlyData?.reduce((best, current) => {
      return current?.winRate > best?.winRate ? current : best;
    }, { winRate: 0, month: 'N/A', year: currentYear });

    // Pie chart data
    const pieChartData = [
      { name: 'Won', value: totalWon, fill: '#10B981' },
      { name: 'Lost', value: totalLost, fill: '#EF4444' },
      { name: 'In Progress', value: deals?.filter(d => !['won', 'lost']?.includes(d?.stage))?.length, fill: '#F59E0B' }
    ];

    setPieData(pieChartData);
    setMetrics({
      totalWon,
      totalLost,
      winRate: overallWinRate,
      bestMonth: {
        rate: bestMonthData?.winRate,
        month: `${bestMonthData?.month} ${bestMonthData?.year}`
      }
    });
  }, [deals]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="font-medium text-popover-foreground">{label} {data?.year || new Date()?.getFullYear()}</p>
          <p className="text-sm text-success">Won: {data?.won} deals</p>
          <p className="text-sm text-error">Lost: {data?.lost} deals</p>
          <p className="text-sm text-muted-foreground">Win Rate: {data?.winRate}%</p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0];
      const total = pieData?.reduce((sum, item) => sum + item?.value, 0);
      const percentage = total > 0 ? ((data?.value / total) * 100)?.toFixed(1) : 0;
      
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="font-medium text-popover-foreground">{data?.name}</p>
          <p className="text-sm text-muted-foreground">{data?.value} deals ({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="bg-card border border-border rounded-xl p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Win Rate Analytics</h3>
          <p className="text-sm text-muted-foreground">Monthly win rate trends and distribution</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={chartType === 'line' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartType('line')}
            iconName="TrendingUp"
            iconPosition="left"
          >
            Trend
          </Button>
          <Button
            variant={chartType === 'pie' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartType('pie')}
            iconName="PieChart"
            iconPosition="left"
          >
            Distribution
          </Button>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={winRateData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="month" 
                stroke="#64748B"
                fontSize={12}
              />
              <YAxis 
                stroke="#64748B"
                fontSize={12}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="winRate"
                stroke="#8B5CF6"
                strokeWidth={3}
                dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, stroke: '#8B5CF6', strokeWidth: 2 }}
                animationDuration={1500}
              />
            </LineChart>
          ) : (
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={120}
                paddingAngle={5}
                dataKey="value"
                animationDuration={1000}
              >
                {pieData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.fill} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <div className="w-3 h-3 bg-success rounded-full" />
            <span className="text-sm font-medium text-foreground">Total Won</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{metrics?.totalWon}</p>
          <p className="text-xs text-muted-foreground">deals closed</p>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <div className="w-3 h-3 bg-error rounded-full" />
            <span className="text-sm font-medium text-foreground">Total Lost</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{metrics?.totalLost}</p>
          <p className="text-xs text-muted-foreground">deals lost</p>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Icon name="Target" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Win Rate</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{metrics?.winRate}%</p>
          <p className="text-xs text-muted-foreground">this year</p>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-sm font-medium text-foreground">Best Month</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{metrics?.bestMonth?.rate}%</p>
          <p className="text-xs text-muted-foreground">{metrics?.bestMonth?.month}</p>
        </div>
      </div>
    </motion.div>
  );
};

export default WinRateChart;